import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Redirect, Route, Router, Switch } from "react-router-dom";
import { logMsg } from "../common/Logger";
import withIdleTimer from "../common/WithIdleTimer";
import ErrorScreen from "../errors/ErrorScreen";
import LocalizedInfoClient from "../shared/clients/LocalizedInfoClient";
import Footer from "../shared/Footer/Footer";
import GlobalNavBar from "../shared/globalNavBar/GlobalNavBar";
import "../styles/sharedStyles.scss";
import { getHeaders } from "../utils/AuthorizationUtil";
import {
  AUTHORIZE_BASE_URL,
  BASE_REPORTING_URL,
  BASE_URL,
  RESERVATION_BASE_URL,
} from "../utils/EmpUtil";
import "./App.scss";
import FieldView from "./pure/FieldView";

// Pinned inputs for the prod connectivity probe. IMG markets include
// AUS/ZAF/VNM/THA/PHL; we pin AUS so the same probes run regardless of
// which market the field_view user is scoped to.
const PROBE_MARKET = "AUS";
const PROBE_LANGUAGE = "en_AUS";

function FieldViewApp(props) {
  const [renderApp, setRenderApp] = useState(false);
  const [shouldNavigationBeRestricted, setShouldNavigationBeRestricted] =
    useState(false);
  const [padding, setPaddingState] = useState("70px");
  const [healthChecks, setHealthChecks] = useState([]);

  const userRef = useRef(null);
  const localizedInfoRef = useRef({});

  if (!userRef.current) {
    userRef.current = new FieldView(
      props.userDetails,
      props.history
    );
  }

  const user = userRef.current;

  const isCurrentPathAllowedToShowHeaderAndFooter = useCallback(
    (currentPathName) => {
      // Field view users are locked to the dashboard. Show the chrome only there
      // (and on / which redirects to /dashboard).
      const staticAllowedPaths = ["/", "/dashboard"];

      return staticAllowedPaths.includes(currentPathName);
    },
    []
  );

  // GlobalNavBar / Footer drive their tabs and links off
  // `user.permissions.rolePermissions` via HasPermissionTo. Pass an
  // empty permissions map so every tab/link hides; logout doesn't
  // depend on a permission, so it still renders.
  const chromePermissions = { rolePermissions: {} };
  const navBarUser = { ...user, permissions: chromePermissions };

  const setNavigationRestriction = useCallback(() => {
    const isAllowedPath = isCurrentPathAllowedToShowHeaderAndFooter(
      props.history.location.pathname
    );
    setShouldNavigationBeRestricted(!isAllowedPath);
  }, [props.history.location.pathname, isCurrentPathAllowedToShowHeaderAndFooter]);

  function navigateToRoute(link) {
    props.history.push(`/${link}`);
  }

  function setPadding(value) {
    if (value > 70) {
      setPaddingState(value + "px");
    }
  }

  // Fire a small set of authenticated GETs against the same hosts a
  // real dealer/nsc session would touch. We surface the HTTP status on
  // the landing page so CORS/auth issues are visible at a glance.
  const runProdConnectivityProbes = useCallback(() => {
    const probes = [
      {
        name: "Authorize",
        service: "Authorize Service",
        method: "POST",
        url: `${AUTHORIZE_BASE_URL}/authorize`,
        body: {},
      },
      {
        name: "Localized info",
        service: "Authorize Service",
        method: "GET",
        url: `${AUTHORIZE_BASE_URL}/localized-info`,
      },
      {
        name: "Translations",
        service: "Translation Service",
        method: "GET",
        url: `${AUTHORIZE_BASE_URL}/translations/${PROBE_LANGUAGE}/${PROBE_MARKET}`,
      },
      {
        name: "NSC is-onboarded",
        service: "IMG Service",
        method: "GET",
        url: `${BASE_URL}/nsc/${PROBE_MARKET}/is-onboarded`,
      },
      {
        name: "Cancel order reasons",
        service: "Reservation Service",
        method: "GET",
        url: `${RESERVATION_BASE_URL}/dealer/cancelOrderReasons?lang=en_ZA&reservationReasons=true`,
      },
      {
        name: "Dealer reservations report",
        service: "Reporting Service",
        method: "POST",
        url: `${BASE_REPORTING_URL}/reporting/dealer/img-reservations-report`,
        body: {
          marketId: PROBE_MARKET,
          commonIds: "AUS|21810|A06454|F",
          langCode: PROBE_LANGUAGE,
        },
      },
    ];

    setHealthChecks(
      probes.map((p) => ({ ...p, status: "pending", ok: null, error: null }))
    );

    probes.forEach((probe, idx) => {
      const headers = getHeaders();
      const request =
        probe.method === "POST"
          ? axios.post(probe.url, probe.body || {}, headers)
          : axios.get(probe.url, headers);

      request
        .then((response) => {
          setHealthChecks((prev) => {
            const next = [...prev];
            next[idx] = {
              ...probe,
              status: response.status,
              ok: response.status >= 200 && response.status < 300,
              error: null,
            };
            return next;
          });
        })
        .catch((error) => {
          const status = error?.response?.status || "ERR";
          const isCors =
            !error?.response &&
            (error?.message || "").toLowerCase().includes("network");
          setHealthChecks((prev) => {
            const next = [...prev];
            next[idx] = {
              ...probe,
              status,
              ok: false,
              error: isCors
                ? "Network/CORS error (no response)"
                : error?.message || "Request failed",
            };
            return next;
          });
        });
    });
  }, []);

  // Field view is a read-only persona used to verify prod auth + CORS.
  // We still call /localized-info to exercise a second cross-origin endpoint
  // beyond /authorize, but failures are non-fatal.
  const getLocalizedInfo = useCallback(() => {
    props.hideOrShow(true);
    LocalizedInfoClient.getLocalizedInfo(user)
      .then((response) => {
        localizedInfoRef.current = Object.assign(
          {},
          { ...localizedInfoRef.current },
          { ...response }
        );
      })
      .catch((error) => logMsg("the error is: ", error))
      .finally(() => {
        setNavigationRestriction();
        setRenderApp(true);
        props.hideOrShow(false);
        runProdConnectivityProbes();
      });
  }, [props, user, setNavigationRestriction, runProdConnectivityProbes]);

  useEffect(() => {
    getLocalizedInfo();
  }, []);

  useEffect(() => {
    const unlisten = props.history.listen(() => setNavigationRestriction());
    return () => {
      unlisten();
    };
  }, [props.history, setNavigationRestriction]);

  const statusColor = (check) => {
    if (check.ok === true) return "#1e7e34";
    if (check.ok === false) return "#c62828";
    return "#666";
  };

  const FieldViewLanding = () => (
    <div className="pageWrapper" data-testid="fieldViewLanding">
      <div style={{ padding: "32px" }}>
        <h2>Welcome, {user.fullName || user.userId}</h2>
        <p>
          You are signed in as a field view user. This view is read-only and is
          intended for verifying production access.
        </p>
        <ul>
          <li>
            <strong>User ID:</strong> {user.userId}
          </li>
          <li>
            <strong>Market:</strong> {user.market}
          </li>
          <li>
            <strong>Roles:</strong> {(user.roles || []).join(", ") || "—"}
          </li>
        </ul>

        <h3 style={{ marginTop: "32px" }}>Prod connectivity check</h3>
        <p style={{ color: "#555", marginTop: "-8px" }}>
          Authenticated requests against the same hosts a normal session would
          touch. 2xx = CORS + auth are healthy on that endpoint.
        </p>
        <table
          data-testid="healthChecksTable"
          style={{
            borderCollapse: "collapse",
            width: "100%",
            maxWidth: "960px",
          }}
        >
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "1px solid #ccc" }}>
              <th style={{ padding: "8px 12px" }}>Service</th>
              <th style={{ padding: "8px 12px" }}>Endpoint</th>
              <th style={{ padding: "8px 12px" }}>URL</th>
              <th style={{ padding: "8px 12px" }}>Status</th>
              <th style={{ padding: "8px 12px" }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {healthChecks.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: "12px", color: "#666" }}>
                  Running checks…
                </td>
              </tr>
            )}
            {healthChecks.map((check) => (
              <tr key={check.name} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "8px 12px" }}>{check.service}</td>
                <td style={{ padding: "8px 12px" }}>
                  {check.method ? `${check.method} ` : ""}
                  {check.name}
                </td>
                <td
                  style={{
                    padding: "8px 12px",
                    fontFamily: "monospace",
                    fontSize: "12px",
                    wordBreak: "break-all",
                  }}
                >
                  {check.url}
                </td>
                <td
                  style={{
                    padding: "8px 12px",
                    color: statusColor(check),
                    fontWeight: 600,
                  }}
                >
                  {check.status}
                </td>
                <td style={{ padding: "8px 12px", color: "#555" }}>
                  {check.error || (check.ok ? "OK" : "")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    renderApp && (
      <Router history={props.history}>
        <div className="appWrapper" id="appWrapper">
          {!shouldNavigationBeRestricted && (
            <div style={{ paddingTop: padding }}>
              <GlobalNavBar
                {...props}
                user={navBarUser}
                urlPath={window.location.pathname}
                navigateToRoute={navigateToRoute}
                setPadding={setPadding}
              />
            </div>
          )}

          <Switch>
            <Route
              exact
              path="/dashboard"
              component={withIdleTimer(FieldViewLanding)}
            />

            <Route
              exact
              path="/"
              render={() => <Redirect to={{ pathname: "/dashboard" }} />}
            />

            <Route
              exact
              path="/error"
              render={(routeProps) => <ErrorScreen {...routeProps} />}
            />
            <Route render={() => <Redirect to={{ pathname: "/dashboard" }} />} />
          </Switch>

          {!shouldNavigationBeRestricted && (
            <Footer permissions={chromePermissions} />
          )}
        </div>
      </Router>
    )
  );
}

export default FieldViewApp;
